import { AiMasteringResponse } from "@/app/api/ai-mastering/route";

export const getFileNameWithoutExtension = (filePath: string): string => {
  const fileName = filePath.split(/[/\\]/).pop() || filePath;
  return fileName.replace(/\.[^/.]+$/, "");
};

const FILE_SIZE_LIMIT = 200 * 1024 * 1024; // 200MB
const MAX_DURATION = 12 * 60; // 12 minutes in seconds

export const validateMusicFile = (file: File): Promise<string | null> => {
  if (file.size > FILE_SIZE_LIMIT) {
    return Promise.resolve("File size exceeds 200MB.");
  }

  return new Promise((resolve) => {
    const audio = new Audio();
    const objectUrl = URL.createObjectURL(file);
    audio.src = objectUrl;

    audio.onloadedmetadata = () => {
      const isValid = audio.duration <= MAX_DURATION;
      URL.revokeObjectURL(objectUrl);
      resolve(isValid ? null : "File duration exceeds 12 minutes.");
    };

    audio.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve("Unable to validate audio file.");
    };
  });
};

const handleMasteringRequest = async (
  endpoint: string,
  formData: FormData,
): Promise<AiMasteringResponse | undefined> => {
  try {
    const res = await fetch(endpoint, {
      method: "POST",
      body: formData,
    });

    if (!res.ok) return undefined;

    return (await res.json()) as AiMasteringResponse;
  } catch (error) {
    return undefined;
  }
};

export const masterPreset = async (
  targetFile: File,
  presetValue: string,
): Promise<AiMasteringResponse | undefined> => {
  const validationError = await validateMusicFile(targetFile);
  if (validationError) {
    return undefined;
  }

  const formData = new FormData();
  formData.append("songFile", targetFile);
  formData.append("presetName", presetValue);

  return handleMasteringRequest("/api/ai-mastering", formData);
};

export const masterReference = async (
  targetFile: File,
  referenceFile: File,
): Promise<AiMasteringResponse | undefined> => {
  const [targetValidation, referenceValidation] = await Promise.all([
    validateMusicFile(targetFile),
    validateMusicFile(referenceFile),
  ]);

  if (targetValidation) {
    return undefined;
  }

  if (referenceValidation) {
    return undefined;
  }

  const formData = new FormData();
  formData.append("masteringFile", targetFile);
  formData.append("referenceFile", referenceFile);

  return handleMasteringRequest("/api/ai-mastering/reference", formData);
};
